import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Input, Row, Select, Space, Statistic, Table, Tabs, Tooltip, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import stc from 'string-to-color';
import { clientDaySeries } from './clientDaySeries';
import { getPhotoShootSessionDate } from '../../utils/photoShootSessionDate';
import ExportExcel from '../../utils/Export-Excel';
import { fetchRevenueItems, useRecomputeRevenue, useRevenueDaily, useRevenueItems, useRevenueRange } from '../../hooks/useRevenue';

const SHOOT_TYPES = [
    'OnModel',
    'Ghost',
    'Flatlay',
    'PD_Shoes',
    'PD_Bags',
    'PD_Accessories',
    'Video',
    '3D_DAR_SCAN',
    '3D_DAR_GSPLAT',
    '3D_DAR_ALLSIDES',
    'GTOM',
    'Extras',
];

function eurAndUsd(eur, rate) {
    const euros = Number(eur || 0);
    const euroText = `€${euros.toFixed(2)}`;
    if (rate == null || Number.isNaN(Number(rate))) return euroText;
    const dollars = Math.round(euros * Number(rate) * 100) / 100;
    return `${euroText} · $${dollars.toFixed(2)}`;
}

function usdNumber(eur, rate) {
    if (rate == null || eur == null || eur === '') return '';
    return (Math.round(Number(eur) * Number(rate) * 100) / 100).toFixed(2);
}

function usdValue(eur, rate) {
    const text = usdNumber(eur, rate);
    return text === '' ? '' : Number(text);
}

const CATEGORY_HINTS = {
    Completed: 'SKUs the photographer finished, including ones that are back in reshoot. Each SKU is in one category below, and those categories add up to this.',
    'In revenue': 'First time this SKU was shot on that bay type. This is the shoot that earns the revenue.',
    'Redid the shoot': 'Shot again on a bay type it had already been shot on. The earlier shoot keeps the revenue.',
    'Previously billed': 'A recurring product whose barcode was already delivered, so this shoot is not billed again.',
    'No charge': 'First time on this bay type, but the price is €0. The article was already priced on the other bay type, or it is a support article.',
    Unmatched: 'First time on a bay type, and no rate line matched. Priced with the unmatched average when one is set.',
    'Not realized': 'What completed SKUs that did not earn would have been worth if this had been the first time on that bay type. A repeat later in the month still counts. Shown separately and not included in gross.',
    'No rate card': 'Photographer-finished SKUs whose client has no rate card effective on that shoot day. On a range, a SKU shot on two such days counts twice.',
};

function HintTitle({ label, hint }) {
    if (!hint) return label;
    return (
        <span>
            {label}{' '}
            <Tooltip title={hint}>
                <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
            </Tooltip>
        </span>
    );
}

const SHOT_SPLIT = [
    ['shotRevenue', 'In revenue'],
    ['shotRedid', 'Redid the shoot'],
    ['shotPreviouslyBilled', 'Previously billed'],
    ['shotNoCharge', 'No charge'],
    ['shotUnmatched', 'Unmatched'],
];

function splitFields(row) {
    const out = {};
    for (const [field, title] of SHOT_SPLIT) out[title] = row?.[field] || 0;
    return out;
}

function SummaryCards({ summary, eurToUsd }) {
    if (!summary) return null;
    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Row gutter={16}>
                <Col span={5}><Card><Statistic title="Gross" value={summary.gross} precision={2} prefix="€" /><div>{eurToUsd == null ? '' : `$${usdNumber(summary.gross, eurToUsd)}`}</div></Card></Col>
                <Col span={5}>
                    <Card style={{ background: '#fff7e6' }}>
                        <Statistic title={<HintTitle label="Not realized" hint={CATEGORY_HINTS['Not realized']} />} value={summary.shotMissedGross || 0} precision={2} prefix="€" />
                        <div style={{ color: '#ad6800' }}>{summary.shotMissed || 0} SKUs, not in gross</div>
                    </Card>
                </Col>
                <Col span={5}><Card><Statistic title={<HintTitle label="Completed SKUs" hint={CATEGORY_HINTS.Completed} />} value={summary.shotSkuCount ?? summary.skuCount} /><div>Finished by the photographer. The next row adds up to this.</div></Card></Col>
                <Col span={4}><Card><Statistic title={<HintTitle label="Unmatched" hint={CATEGORY_HINTS.Unmatched} />} value={summary.shotUnmatched ?? summary.unmatchedCount ?? 0} /><div>First time on a bay type, no rate line</div></Card></Col>
                <Col span={5}>
                    <Card style={{ background: '#fff1f0' }}>
                        <Statistic title={<HintTitle label="Previously billed" hint={CATEGORY_HINTS['Previously billed']} />} value={summary.previouslyBilledSkuCount || 0} />
                        <div style={{ color: '#a8071a' }}>{eurAndUsd(summary.previouslyBilledGross, eurToUsd)} not in the total</div>
                    </Card>
                </Col>
            </Row>
            {summary.shotSkuCount != null && (
                <Row gutter={16}>
                    {SHOT_SPLIT.map(([field, title]) => (
                        <Col span={4} key={field}><Card><Statistic title={<HintTitle label={title} hint={CATEGORY_HINTS[title]} />} value={summary[field] || 0} /></Card></Col>
                    ))}
                </Row>
            )}
        </Space>
    );
}

function NoRateCard({ rows }) {
    const list = rows || [];
    return (
        <Card title={<HintTitle label="No rate card" hint={CATEGORY_HINTS['No rate card']} />}>
            {list.length === 0 ? (
                <span>Every client that was shot has a rate card.</span>
            ) : (
                <Table
                    size="small"
                    pagination={false}
                    rowKey="companyCode"
                    dataSource={list}
                    columns={[
                        { title: 'Client', dataIndex: 'companyCode' },
                        { title: 'SKUs shot', dataIndex: 'skuCount' },
                    ]}
                />
            )}
        </Card>
    );
}

function axisMoney(value, max) {
    if (max >= 10000) return `€${Math.round(value / 1000)}k`;
    if (max >= 10) return `€${Math.round(value)}`;
    return `€${value.toFixed(2)}`;
}

function ClientRevenueChart({ rows, from, to }) {
    const [hover, setHover] = useState(null);
    const [hidden, setHidden] = useState(() => new Set());
    const { days, series } = clientDaySeries(rows, from, to);
    if (!series.length) return null;
    const visible = series.filter(line => !hidden.has(line.client));
    const width = 960;
    const height = 280;
    const pad = { left: 64, right: 16, top: 16, bottom: days.length > 10 ? 56 : 32 };
    const innerWidth = width - pad.left - pad.right;
    const innerHeight = height - pad.top - pad.bottom;
    const dayTotals = days.map((_, index) => visible.reduce((sum, line) => sum + (line.points[index] || 0), 0));
    const max = Math.max(0.01, ...dayTotals);
    const slot = innerWidth / Math.max(days.length, 1);
    const barWidth = Math.max(1, Math.min(48, slot * 0.7));
    const center = index => pad.left + slot * index + slot / 2;
    const y = value => pad.top + innerHeight - (value / max) * innerHeight;
    const labelEvery = days.length > 31 ? Math.ceil(days.length / 12) : days.length > 14 ? 2 : 1;
    const hovered = hover == null ? null : days[hover];
    const hoverClients = hovered
        ? visible
            .map(line => ({ client: line.client, value: line.points[hover] }))
            .filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value)
        : [];
    const toggleClient = client => {
        setHidden(current => {
            const next = new Set(current);
            if (next.has(client)) next.delete(client);
            else next.add(client);
            return next;
        });
    };

    return (
        <Card title="Revenue by day">
            <div style={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
                <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Stacked revenue by day and client">
                    {[0, 0.25, 0.5, 0.75, 1].map(step => {
                        const value = max * step;
                        const yLine = y(value);
                        return (
                            <g key={step}>
                                <line x1={pad.left} y1={yLine} x2={pad.left + innerWidth} y2={yLine} stroke="#f0f0f0" />
                                <text x={pad.left - 8} y={yLine + 4} textAnchor="end" fontSize="11" fill="#8c8c8c">
                                    {axisMoney(value, max)}
                                </text>
                            </g>
                        );
                    })}
                    {days.map((day, index) => {
                        let stacked = 0;
                        return (
                            <g key={day}>
                                {visible.map(line => {
                                    const value = line.points[index] || 0;
                                    const bottom = stacked;
                                    stacked += value;
                                    if (value <= 0) return null;
                                    const yTop = y(stacked);
                                    return (
                                        <rect
                                            key={line.client}
                                            x={center(index) - barWidth / 2}
                                            y={yTop}
                                            width={barWidth}
                                            height={Math.max(y(bottom) - yTop, 0)}
                                            fill={stc(line.client)}
                                        >
                                            <title>{line.client}</title>
                                        </rect>
                                    );
                                })}
                            </g>
                        );
                    })}
                    {days.map((day, index) => {
                        if (index % labelEvery !== 0 && index !== days.length - 1) return null;
                        const label = dayjs(day).format(days.length > 14 ? 'MM-DD' : 'MMM D');
                        return (
                            <text
                                key={day}
                                x={center(index)}
                                y={height - 8}
                                textAnchor={days.length > 10 ? 'end' : 'middle'}
                                fontSize="11"
                                fill="#8c8c8c"
                                transform={days.length > 10 ? `rotate(-40 ${center(index)} ${height - 8})` : undefined}
                            >
                                {label}
                            </text>
                        );
                    })}
                    {days.map((day, index) => (
                        <rect
                            key={`${day}-hit`}
                            x={pad.left + slot * index}
                            y={pad.top}
                            width={Math.max(slot, 1)}
                            height={innerHeight}
                            fill={hover === index ? 'rgba(0,0,0,0.04)' : 'transparent'}
                            onMouseEnter={() => setHover(index)}
                        />
                    ))}
                </svg>
                {hovered && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 8,
                            left: `${(center(hover) / width) * 100}%`,
                            transform: hover === 0 && days.length > 1 ? 'translateX(0)' : hover === days.length - 1 && days.length > 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                            background: '#fff',
                            border: '1px solid #f0f0f0',
                            borderRadius: 6,
                            padding: '8px 10px',
                            fontSize: 12,
                            pointerEvents: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{hovered}</div>
                        {hoverClients.length === 0 && <div>€0.00</div>}
                        {hoverClients.map(item => (
                            <div key={item.client} style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
                                <span>
                                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 8, background: stc(item.client), marginRight: 6 }} />
                                    {item.client}
                                </span>
                                <span>€{item.value.toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 8 }}>
                {series.map(line => {
                    const off = hidden.has(line.client);
                    return (
                        <button
                            key={line.client}
                            type="button"
                            aria-pressed={!off}
                            onClick={() => toggleClient(line.client)}
                            style={{
                                font: 'inherit',
                                fontSize: 12,
                                border: 0,
                                background: 'transparent',
                                padding: 0,
                                cursor: 'pointer',
                                color: off ? '#bfbfbf' : 'inherit',
                            }}
                        >
                            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 10, background: off ? '#d9d9d9' : stc(line.client), marginRight: 6 }} />
                            <span style={{ textDecoration: off ? 'line-through' : 'none' }}>{line.client}</span>
                        </button>
                    );
                })}
            </div>
        </Card>
    );
}

function splitColumns() {
    return SHOT_SPLIT.map(([field, title]) => ({
        title: <HintTitle label={title} hint={CATEGORY_HINTS[title]} />,
        dataIndex: field,
        render: value => value || 0,
    }));
}

function moneyFields(row, rate) {
    return {
        Completed: row.shotSkuCount ?? row.skuCount ?? 0,
        ...splitFields(row),
        'Not realized SKUs': row.shotMissed || 0,
        'Not realized EUR': Number(row.shotMissedGross) || 0,
        'Gross EUR': Number(row.gross) || 0,
        'Gross USD': usdValue(row.gross, rate),
    };
}

function errorText(error) {
    const body = error?.response?.data?.message;
    if (typeof body === 'string') return body;
    return 'Request failed';
}

function flatItem(row, rate) {
    return {
        SKU: row.snapshot?.skuName,
        EAN: row.snapshot?.ean,
        Client: row.companyCode,
        'Bay type': row.bayType,
        Bay: row.bayEmail,
        'Shoot types': (row.snapshot?.shootTypes || []).join(', '),
        Line: row.pricing?.label,
        Rate: row.pricing?.rate,
        'Gross EUR': Number(row.pricing?.gross) || 0,
        'Gross USD': usdValue(row.pricing?.gross, rate),
        Matched: row.pricing?.matched ? 'yes' : 'no',
        Category: row.category || '',
    };
}

export default function Revenue() {
    const studioToday = getPhotoShootSessionDate();
    const [date, setDate] = useState(studioToday);
    const [bayType, setBayType] = useState();
    const [companyCode, setCompanyCode] = useState('');
    const [bayEmail, setBayEmail] = useState('');
    const [shootType, setShootType] = useState();
    const [matched, setMatched] = useState();
    const [csvField, setCsvField] = useState('');
    const [csvValue, setCsvValue] = useState('');
    const [pageNum, setPageNum] = useState(1);
    const [rangeFrom, setRangeFrom] = useState(dayjs(studioToday).subtract(6, 'day').format('YYYY-MM-DD'));
    const [rangeTo, setRangeTo] = useState(studioToday);
    const [groupBy, setGroupBy] = useState('day');
    const [tab, setTab] = useState('day');
    const [exportBook, setExportBook] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [category, setCategory] = useState();
    const [skuQuery, setSkuQuery] = useState('');

    const filters = {
        date,
        bayType,
        companyCode: companyCode || undefined,
        bayEmail: bayEmail || undefined,
        shootType,
        matched,
        csvField: csvField || undefined,
        csvValue: csvField ? csvValue : undefined,
        shots: true,
        pageNum: 1,
        pageSize: 5000,
    };
    const daily = useRevenueDaily(
        { date, bayType, companyCode: filters.companyCode, bayEmail: filters.bayEmail },
        tab === 'day'
    );
    const itemQuery = useRevenueItems(filters, tab === 'day');
    const skuNeedle = skuQuery.trim().toLowerCase();
    const shotRows = (itemQuery.data?.items || []).filter(row => {
        if (category && row.category !== category) return false;
        if (!skuNeedle) return true;
        const name = String(row.snapshot?.skuName || '').toLowerCase();
        const ean = String(row.snapshot?.ean || '').toLowerCase();
        return name.includes(skuNeedle) || ean.includes(skuNeedle);
    });
    const pageRows = shotRows.slice((pageNum - 1) * 50, pageNum * 50);
    const range = useRevenueRange({ from: rangeFrom, to: rangeTo, groupBy }, tab === 'range');
    const dayRange = useRevenueRange({ from: rangeFrom, to: rangeTo, groupBy: 'day' }, tab === 'range');
    const recompute = useRecomputeRevenue();
    const summary = daily.data?.summary;
    const eurToUsd = (tab === 'range' ? range.data?.eurToUsd : daily.data?.eurToUsd) ?? null;
    const fxAsOf = (tab === 'range' ? range.data?.fxAsOf : daily.data?.fxAsOf) || null;
    const maxGross = Math.max(...((range.data?.rows || []).map(row => Number(row.gross) || 0)), 1);

    const runRecompute = () => {
        recompute.mutate(
            { from: date, to: date },
            {
                onSuccess: () => message.success(`Recomputed ${date}`),
                onError: error => message.error(errorText(error)),
            }
        );
    };

    useEffect(() => {
        setExportBook(null);
    }, [date, bayType, companyCode, bayEmail, shootType, matched, csvField, csvValue, category, skuQuery]);

    const loadExport = async () => {
        setExporting(true);
        setExportBook(null);
        try {
            const scope = {
                date,
                bayType,
                companyCode: companyCode || undefined,
                bayEmail: bayEmail || undefined,
            };
            const loadAll = async extra => {
                const pageSize = 5000;
                const items = [];
                let page = 1;
                let total = 0;
                do {
                    const data = await fetchRevenueItems({ ...scope, ...extra, pageNum: page, pageSize });
                    const batch = data?.items || [];
                    total = Number(data?.total) || 0;
                    items.push(...batch);
                    page += 1;
                    if (!batch.length) break;
                } while (items.length < total);
                return items;
            };
            const items = await loadAll({ shots: true });
            const daySummary = summary || {};
            setExportBook([
                {
                    name: 'Summary',
                    rows: [{
                        Date: date,
                        ...moneyFields(daySummary, eurToUsd),
                        'EUR per USD': eurToUsd == null ? '' : Number(Number(eurToUsd).toFixed(6)),
                        'FX as of': fxAsOf || '',
                    }],
                },
                {
                    name: 'By bay type',
                    headers: ['Bay type', 'Completed', 'In revenue', 'Redid the shoot', 'Previously billed', 'No charge', 'Unmatched', 'Not realized EUR', 'Gross EUR', 'Gross USD'],
                    rows: (daySummary.byBayType || []).map(row => ({ 'Bay type': row.bayType, Completed: row.shotSkuCount || 0, ...splitFields(row), 'Not realized EUR': Number(row.shotMissedGross) || 0, 'Gross EUR': Number(row.gross) || 0, 'Gross USD': usdValue(row.gross, eurToUsd) })),
                },
                {
                    name: 'By client',
                    headers: ['Client', 'Completed', 'In revenue', 'Redid the shoot', 'Previously billed', 'No charge', 'Unmatched', 'Not realized EUR', 'Gross EUR', 'Gross USD'],
                    rows: (daySummary.byClient || []).map(row => ({ Client: row.companyCode, Completed: row.shotSkuCount || 0, ...splitFields(row), 'Not realized EUR': Number(row.shotMissedGross) || 0, 'Gross EUR': Number(row.gross) || 0, 'Gross USD': usdValue(row.gross, eurToUsd) })),
                },
                {
                    name: 'By bay',
                    headers: ['Bay', 'Bay type', 'Completed', 'In revenue', 'Redid the shoot', 'Previously billed', 'No charge', 'Unmatched', 'Not realized EUR', 'Gross EUR', 'Gross USD'],
                    rows: (daySummary.byBay || []).map(row => ({ Bay: row.bayEmail, 'Bay type': row.bayType, Completed: row.shotSkuCount || 0, ...splitFields(row), 'Not realized EUR': Number(row.shotMissedGross) || 0, 'Gross EUR': Number(row.gross) || 0, 'Gross USD': usdValue(row.gross, eurToUsd) })),
                },
                {
                    name: 'By SKU',
                    headers: ['SKU', 'EAN', 'Client', 'Bay type', 'Bay', 'Shoot types', 'Line', 'Rate', 'Gross EUR', 'Gross USD', 'Matched', 'Category'],
                    rows: items.map(row => flatItem(row, eurToUsd)),
                },
            ]);
        } catch (error) {
            message.error(errorText(error));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div style={{ padding: 16 }}>
            <Tabs
                activeKey={tab}
                onChange={setTab}
                items={[
                    {
                        key: 'day',
                        label: 'Day',
                        children: (
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <Space wrap>
                                    <DatePicker value={date ? dayjs(date) : null} onChange={value => { setDate(value ? value.format('YYYY-MM-DD') : ''); setPageNum(1); }} />
                                    <Select
                                        allowClear
                                        placeholder="Bay type"
                                        style={{ width: 140 }}
                                        value={bayType}
                                        onChange={value => { setBayType(value); setPageNum(1); }}
                                        options={['model', 'product', 'scanning', 'unknown'].map(value => ({ value, label: value }))}
                                    />
                                    <Input placeholder="Client code" value={companyCode} onChange={event => { setCompanyCode(event.target.value); setPageNum(1); }} style={{ width: 140 }} />
                                    <Input placeholder="Bay email" value={bayEmail} onChange={event => { setBayEmail(event.target.value); setPageNum(1); }} style={{ width: 200 }} />
                                    <Select
                                        allowClear
                                        placeholder="Shoot type"
                                        style={{ width: 160 }}
                                        value={shootType}
                                        onChange={value => { setShootType(value); setPageNum(1); }}
                                        options={SHOOT_TYPES.map(value => ({ value, label: value }))}
                                    />
                                    <Select
                                        allowClear
                                        placeholder="Matched"
                                        style={{ width: 130 }}
                                        value={matched}
                                        onChange={value => { setMatched(value); setPageNum(1); }}
                                        options={[{ value: true, label: 'Matched' }, { value: false, label: 'Unmatched' }]}
                                    />
                                    <Input placeholder="csvData field" value={csvField} onChange={event => setCsvField(event.target.value)} style={{ width: 160 }} />
                                    <Input placeholder="csv value" value={csvValue} onChange={event => setCsvValue(event.target.value)} style={{ width: 140 }} />
                                    <Button onClick={runRecompute} loading={recompute.isLoading}>Recompute day</Button>
                                    <Button onClick={loadExport} loading={exporting}>Prepare Excel</Button>
                                    {eurToUsd != null && <span>1 EUR = ${Number(eurToUsd).toFixed(4)}{fxAsOf ? ` (${fxAsOf})` : ''}</span>}
                                    {exportBook && <ExportExcel sheets={exportBook} fileName={`revenue-${date}`} />}
                                </Space>
                                {(daily.error || itemQuery.error) && <Alert type="error" message={errorText(daily.error || itemQuery.error)} />}
                                {!daily.isFetching && !summary && <Alert type="info" message="No summary for this day yet. Recompute it." />}
                                <SummaryCards summary={summary} eurToUsd={eurToUsd} />
                                {daily.data && <NoRateCard rows={daily.data.noRateCard} />}
                                <Card title="By client">
                                    <Table size="small" rowKey="companyCode" pagination={false} dataSource={summary?.byClient || []} columns={shotTableColumns('companyCode', 'Client', eurToUsd)} />
                                </Card>
                                <Card title="By bay type">
                                    <Table size="small" rowKey="bayType" pagination={false} dataSource={summary?.byBayType || []} columns={shotTableColumns('bayType', 'Bay type', eurToUsd)} />
                                </Card>
                                <Card title="By bay">
                                    <Table size="small" rowKey={row => `${row.bayEmail}|${row.bayType}`} pagination={false} dataSource={summary?.byBay || []} columns={[{ title: 'Bay', dataIndex: 'bayEmail' }, ...shotTableColumns('bayType', 'Type', eurToUsd)]} />
                                </Card>
                                <Card
                                    title="SKUs"
                                    extra={(
                                        <Space>
                                            <Select
                                                allowClear
                                                placeholder="Category"
                                                style={{ width: 180 }}
                                                value={category}
                                                onChange={value => { setCategory(value); setPageNum(1); }}
                                                options={SHOT_SPLIT.map(([, title]) => ({ value: title, label: title }))}
                                            />
                                            <Input
                                                allowClear
                                                placeholder="SKU or EAN"
                                                value={skuQuery}
                                                onChange={event => { setSkuQuery(event.target.value); setPageNum(1); }}
                                                style={{ width: 180 }}
                                            />
                                        </Space>
                                    )}
                                >
                                    <Table
                                        size="small"
                                        rowKey={row => String(row.skuId)}
                                        loading={itemQuery.isFetching}
                                        dataSource={pageRows}
                                        pagination={{
                                            current: pageNum,
                                            pageSize: 50,
                                            total: shotRows.length,
                                            onChange: setPageNum,
                                            showSizeChanger: false,
                                        }}
                                        columns={[
                                            {
                                                title: '#',
                                                width: 64,
                                                render: (_, __, index) => (pageNum - 1) * 50 + index + 1,
                                            },
                                            { title: 'SKU', render: (_, row) => row.snapshot?.skuName },
                                            { title: 'EAN', render: (_, row) => row.snapshot?.ean },
                                            { title: 'Client', dataIndex: 'companyCode' },
                                            { title: 'Category', dataIndex: 'category' },
                                            { title: 'Bay type', dataIndex: 'bayType' },
                                            { title: 'Bay', dataIndex: 'bayEmail' },
                                            { title: 'Shoot types', render: (_, row) => (row.snapshot?.shootTypes || []).join(', ') },
                                            { title: 'Line', render: (_, row) => row.pricing?.label || '—' },
                                            { title: 'Rate', render: (_, row) => eurAndUsd(row.pricing?.gross, eurToUsd) },
                                            { title: 'Matched', render: (_, row) => (row.pricing?.matched ? 'yes' : 'no') },
                                        ]}
                                    />
                                </Card>
                            </Space>
                        ),
                    },
                    {
                        key: 'range',
                        label: 'Range',
                        children: (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Space wrap>
                                    <DatePicker value={rangeFrom ? dayjs(rangeFrom) : null} onChange={value => setRangeFrom(value ? value.format('YYYY-MM-DD') : '')} />
                                    <DatePicker value={rangeTo ? dayjs(rangeTo) : null} onChange={value => setRangeTo(value ? value.format('YYYY-MM-DD') : '')} />
                                    <Select
                                        value={groupBy}
                                        onChange={setGroupBy}
                                        style={{ width: 180 }}
                                        options={RANGE_GROUPS}
                                    />
                                    {(range.data?.rows || []).length > 0 && (
                                        <ExportExcel
                                            excelData={rangeExportRows(range.data.rows, groupBy, eurToUsd)}
                                            fileName={`revenue-${rangeFrom}-to-${rangeTo}-${groupBy}`}
                                        />
                                    )}
                                </Space>
                                {range.error && <Alert type="error" message={errorText(range.error)} />}
                                {groupBy !== 'day' && dayRange.error && <Alert type="error" message={errorText(dayRange.error)} />}
                                <SummaryCards summary={range.data?.summary} eurToUsd={eurToUsd} />
                                {range.data && <NoRateCard rows={range.data.noRateCard} />}
                                <ClientRevenueChart rows={dayRange.data?.rows} from={rangeFrom} to={rangeTo} />
                                <Table
                                    size="small"
                                    loading={range.isFetching}
                                    rowKey={row => JSON.stringify(row.shootDate || row.key)}
                                    pagination={false}
                                    dataSource={range.data?.rows || []}
                                    columns={[
                                        { title: 'Group', render: (_, row) => rangeIdentity(row, groupBy).group },
                                        { title: 'Gross EUR', dataIndex: 'gross', render: value => `€${Number(value || 0).toFixed(2)}` },
                                        { title: 'Gross USD', render: (_, row) => { const usd = usdNumber(row.gross, eurToUsd); return usd === '' ? '—' : `$${usd}`; } },
                                        { title: <HintTitle label="Completed" hint={CATEGORY_HINTS.Completed} />, render: (_, row) => row.shotSkuCount || 0 },
                                        ...splitColumns(),
                                        { title: <HintTitle label="Not realized" hint={CATEGORY_HINTS['Not realized']} />, render: (_, row) => eurAndUsd(row.shotMissedGross, eurToUsd) },
                                        {
                                            title: '',
                                            render: (_, row) => (
                                                <div style={{ background: '#e6f4ff', width: 160 }}>
                                                    <div style={{ background: '#1677ff', height: 10, width: `${((Number(row.gross) || 0) / maxGross) * 100}%` }} />
                                                </div>
                                            ),
                                        },
                                    ]}
                                />
                            </Space>
                        ),
                    },
                ]}
            />
        </div>
    );
}

const RANGE_GROUPS = [
    { value: 'day', label: 'Day' },
    { value: 'client', label: 'Client' },
    { value: 'bayType', label: 'Bay type' },
    { value: 'bay', label: 'Bay' },
    { value: 'clientBayType', label: 'Client + bay type' },
];

function rangeIdentity(row, groupBy) {
    if (groupBy === 'day') return { group: row.shootDate || '', client: '', bayType: '', bay: '' };
    if (groupBy === 'client') return { group: row.key || '', client: row.key || '', bayType: '', bay: '' };
    if (groupBy === 'bayType') return { group: row.key || '', client: '', bayType: row.key || '', bay: '' };
    if (groupBy === 'bay') {
        const bay = row.key?.bayEmail || '';
        const bayType = row.key?.bayType || '';
        return { group: `${bay} ${bayType}`.trim(), client: '', bayType, bay };
    }
    if (groupBy === 'clientBayType') {
        const client = row.key?.companyCode || '';
        const bayType = row.key?.bayType || '';
        return { group: `${client} ${bayType}`.trim(), client, bayType, bay: '' };
    }
    return { group: '', client: '', bayType: '', bay: '' };
}

function rangeExportRows(rows, groupBy, rate) {
    return rows.map(row => {
        const identity = rangeIdentity(row, groupBy);
        return {
            Group: identity.group,
            Client: identity.client,
            'Bay type': identity.bayType,
            Bay: identity.bay,
            'Gross EUR': Number(row.gross) || 0,
            'Gross USD': usdNumber(row.gross, rate) === '' ? '' : Number(usdNumber(row.gross, rate)),
            Completed: row.shotSkuCount || 0,
            ...splitFields(row),
            'Not realized EUR': Number(row.shotMissedGross) || 0,
        };
    });
}

function moneyColumns(key, title, rate, options = {}) {
    const skuField = options.skuField || 'shotSkuCount';
    return [
        { title, dataIndex: key },
        { title: <HintTitle label={options.skuTitle || 'Completed'} hint={CATEGORY_HINTS.Completed} />, dataIndex: skuField, render: (_, row) => row[skuField] || 0 },
        { title: 'Gross', dataIndex: 'gross', render: value => eurAndUsd(value, rate) },
    ];
}

function shotTableColumns(key, title, rate) {
    const base = moneyColumns(key, title, rate);
    return [
        ...base.slice(0, 2),
        ...splitColumns(),
        { title: <HintTitle label="Not realized" hint={CATEGORY_HINTS['Not realized']} />, render: (_, row) => eurAndUsd(row.shotMissedGross, rate) },
        ...base.slice(2),
    ];
}
